/**
 * GraphQL queries for Audit primitive
 *
 * 11 queries covering:
 * - Single audit retrieval
 * - List by tenant/framework/status/period/user/queue
 * - Expiring audits (retention management)
 * - AI-generated audits (Formula #3: AI Human Hybrid)
 * - Approval chain tracking (dual approval)
 * - Federated subgraph validation (Medplum FHIR, PAYROLL, OFBiz)
 */

import { gql } from '@apollo/client';
import {
  AUDIT_FIELDS,
  AUDIT_LIST_RESULT_FIELDS,
  SUBGRAPH_VALIDATION_RESULT_FIELDS,
} from './fragments';

/**
 * Get a single audit by ID
 */
export const GET_AUDIT = gql`
  ${AUDIT_FIELDS}
  query GetAudit($tenantId: String!, $id: ID!) {
    getAudit(tenantId: $tenantId, id: $id) {
      ...AuditFields
    }
  }
`;

/**
 * List all audits for a tenant with pagination
 */
export const LIST_AUDITS = gql`
  ${AUDIT_LIST_RESULT_FIELDS}
  query ListAudits($tenantId: String!, $limit: Int, $nextToken: String) {
    listAudits(tenantId: $tenantId, limit: $limit, nextToken: $nextToken) {
      ...AuditListResultFields
    }
  }
`;

/**
 * List audits by compliance framework (HIPAA, SOX, FDA, GDPR, PCI-DSS)
 */
export const LIST_AUDITS_BY_FRAMEWORK = gql`
  ${AUDIT_LIST_RESULT_FIELDS}
  query ListAuditsByFramework(
    $tenantId: String!
    $frameworkType: AuditFrameworkTypeQL!
    $limit: Int
    $nextToken: String
  ) {
    listAuditsByFramework(
      tenantId: $tenantId
      frameworkType: $frameworkType
      limit: $limit
      nextToken: $nextToken
    ) {
      ...AuditListResultFields
    }
  }
`;

/**
 * List audits by compliance status
 */
export const LIST_AUDITS_BY_STATUS = gql`
  ${AUDIT_LIST_RESULT_FIELDS}
  query ListAuditsByStatus(
    $tenantId: String!
    $status: ComplianceStatusQL!
    $limit: Int
    $nextToken: String
  ) {
    listAuditsByStatus(tenantId: $tenantId, status: $status, limit: $limit, nextToken: $nextToken) {
      ...AuditListResultFields
    }
  }
`;

/**
 * List audits within a compliance period
 */
export const LIST_AUDITS_BY_PERIOD = gql`
  ${AUDIT_LIST_RESULT_FIELDS}
  query ListAuditsByPeriod(
    $tenantId: String!
    $startDate: DateTime!
    $endDate: DateTime!
    $limit: Int
    $nextToken: String
  ) {
    listAuditsByPeriod(
      tenantId: $tenantId
      startDate: $startDate
      endDate: $endDate
      limit: $limit
      nextToken: $nextToken
    ) {
      ...AuditListResultFields
    }
  }
`;

/**
 * List audits by user who generated them
 */
export const LIST_AUDITS_BY_USER = gql`
  ${AUDIT_LIST_RESULT_FIELDS}
  query ListAuditsByUser($tenantId: String!, $userId: String!, $limit: Int, $nextToken: String) {
    listAuditsByUser(tenantId: $tenantId, userId: $userId, limit: $limit, nextToken: $nextToken) {
      ...AuditListResultFields
    }
  }
`;

/**
 * List audits in a specific processing queue (Formula #6: Load-Balanced Queue)
 */
export const LIST_AUDITS_BY_QUEUE = gql`
  ${AUDIT_LIST_RESULT_FIELDS}
  query ListAuditsByQueue($tenantId: String!, $queueId: String!, $limit: Int, $nextToken: String) {
    listAuditsByQueue(
      tenantId: $tenantId
      queueId: $queueId
      limit: $limit
      nextToken: $nextToken
    ) {
      ...AuditListResultFields
    }
  }
`;

/**
 * List audits with retention expiring before a given date
 * Critical for regulatory compliance (HIPAA 6y, SOX 7y, FDA lifetime)
 */
export const LIST_EXPIRING_AUDITS = gql`
  ${AUDIT_LIST_RESULT_FIELDS}
  query ListExpiringAudits(
    $tenantId: String!
    $expiresBefore: DateTime!
    $limit: Int
    $nextToken: String
  ) {
    listExpiringAudits(
      tenantId: $tenantId
      expiresBefore: $expiresBefore
      limit: $limit
      nextToken: $nextToken
    ) {
      ...AuditListResultFields
    }
  }
`;

/**
 * List AI-generated audits (Formula #3: AI Human Hybrid)
 * Supports dual human approval chain tracking
 */
export const LIST_AI_GENERATED_AUDITS = gql`
  ${AUDIT_LIST_RESULT_FIELDS}
  query ListAIGeneratedAudits(
    $tenantId: String!
    $humanReviewed: Boolean
    $limit: Int
    $nextToken: String
  ) {
    listAiGeneratedAudits(
      tenantId: $tenantId
      humanReviewed: $humanReviewed
      limit: $limit
      nextToken: $nextToken
    ) {
      ...AuditListResultFields
    }
  }
`;

/**
 * Get the complete approval chain for an audit (dual approval tracking)
 * Returns sequence of audits in the approval workflow
 */
export const GET_AUDIT_APPROVAL_CHAIN = gql`
  ${AUDIT_FIELDS}
  query GetAuditApprovalChain($tenantId: String!, $auditId: ID!) {
    getAuditApprovalChain(tenantId: $tenantId, auditId: $auditId) {
      ...AuditFields
    }
  }
`;

/**
 * Validate audit against all federated subgraphs
 * Validates against: Medplum FHIR, PAYROLL Payroll, OFBiz ERP
 * Returns validation results per subgraph with errors and warnings
 */
export const VALIDATE_AUDIT_SUBGRAPHS = gql`
  ${SUBGRAPH_VALIDATION_RESULT_FIELDS}
  query ValidateAuditSubgraphs($tenantId: String!, $auditId: ID!) {
    validateAuditSubgraphs(tenantId: $tenantId, auditId: $auditId) {
      ...SubgraphValidationResultFields
    }
  }
`;
